import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanSearch, ListPlus, RefreshCw, Pencil, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const SCAN_STEP_LABELS = {
  official_db: "Checking official card databases...",
  pricing_sites: "Searching pricing sites...",
  ai_draft: "Drafting the list with AI...",
};

export default function ChecklistPanel({ collection, ownedOnly, onToggleOwnedOnly, onUpdated }) {
  const [scanning, setScanning] = useState(false);
  const [scanStep, setScanStep] = useState(null);
  const pollRef = useRef(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [entriesText, setEntriesText] = useState(
    (collection.checklist || []).map((e) => `${e.card_number}, ${e.name}`).join("\n")
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const stopScan = () => {
    clearInterval(pollRef.current);
    setScanning(false);
    setScanStep(null);
  };

  const handleScan = async () => {
    setScanning(true);
    setScanStep("official_db");
    try {
      // Scan runs as a background job server-side (big sets take a while); poll for progress.
      await api.post(`/collections/${collection.id}/checklist/scan`);
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/collections/${collection.id}/checklist/scan/status`);
          const scan = res.data.scan || {};
          if (scan.status === "running") {
            setScanStep(scan.step);
            return;
          }
          stopScan();
          if (scan.status === "failed") {
            toast.error("Scan failed. Please try again.");
            return;
          }
          if (scan.checklist_status === "found") {
            toast.success(`Found ${scan.count} cards in this set's checklist`);
            if (scan.checklist_source === "ai_generated") {
              toast.info("This list was drafted by AI — it may be incomplete. Use 'Edit list' to correct it.");
            }
          } else {
            toast.info(scan.reason || "Couldn't find a checklist for this set yet.");
          }
          onUpdated();
        } catch {
          stopScan();
        }
      }, 2000);
    } catch {
      toast.error("Scan failed. Please try again.");
      stopScan();
    }
  };

  const handleSaveManual = async () => {
    const entries = entriesText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [number, ...rest] = line.split(",");
        return { card_number: number?.trim(), name: rest.join(",").trim() || "Unknown" };
      })
      .filter((e) => e.card_number);

    if (entries.length === 0) {
      toast.error("Add at least one line like: 4, Charizard");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/collections/${collection.id}/checklist`, { entries });
      toast.success(`Saved a ${entries.length}-card checklist`);
      setEditorOpen(false);
      onUpdated();
    } catch {
      toast.error("Couldn't save that checklist. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (collection.checklist_status === "none") {
    return (
      <div data-testid="checklist-panel-none" className="flex items-center justify-between gap-4 border border-white/10 bg-[#111113] rounded-xl p-4 mb-6 flex-wrap">
        <p className="text-sm text-zinc-400">Track missing cards for this collection by finding its full set checklist.</p>
        <div className="flex gap-2">
          <Button data-testid="checklist-scan-button" size="sm" variant="outline" onClick={handleScan} disabled={scanning} className="border-white/20 hover:bg-white/5 rounded-full">
            <ScanSearch className="w-4 h-4 mr-1.5" /> {scanning ? "Scanning..." : "Scan for set"}
          </Button>
          <Button data-testid="checklist-create-manual-button" size="sm" variant="outline" onClick={() => setEditorOpen(true)} className="border-white/20 hover:bg-white/5 rounded-full">
            <ListPlus className="w-4 h-4 mr-1.5" /> Create set manually
          </Button>
        </div>
        {scanning && <ScanProgress step={scanStep} />}
        <ChecklistEditorDialog open={editorOpen} onOpenChange={setEditorOpen} value={entriesText} onChange={setEntriesText} onSave={handleSaveManual} saving={saving} />
      </div>
    );
  }

  return (
    <div data-testid="checklist-panel-active" className="flex items-center justify-between gap-4 border border-white/10 bg-[#111113] rounded-xl p-4 mb-6 flex-wrap">
      <div className="flex items-center gap-3">
        <Switch data-testid="checklist-owned-only-toggle" checked={ownedOnly} onCheckedChange={onToggleOwnedOnly} />
        <div>
          <span className="text-sm text-zinc-300">{ownedOnly ? "Showing owned cards only" : `Showing all ${collection.checklist.length} cards (missing ones grayed out)`}</span>
          {collection.checklist_source === "ai_generated" && (
            <p data-testid="checklist-ai-generated-note" className="text-[11px] text-[#F59E0B] mt-0.5">AI-drafted list — may be incomplete. Use "Edit list" to correct it.</p>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button data-testid="checklist-rescan-button" size="sm" variant="ghost" onClick={handleScan} disabled={scanning} className="text-zinc-400 hover:text-white rounded-full">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-scan
        </Button>
        <Button data-testid="checklist-edit-button" size="sm" variant="ghost" onClick={() => setEditorOpen(true)} className="text-zinc-400 hover:text-white rounded-full">
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit list
        </Button>
      </div>
      {scanning && <ScanProgress step={scanStep} />}
      <ChecklistEditorDialog open={editorOpen} onOpenChange={setEditorOpen} value={entriesText} onChange={setEntriesText} onSave={handleSaveManual} saving={saving} />
    </div>
  );
}

function ScanProgress({ step }) {
  return (
    <div data-testid="checklist-scan-progress" className="w-full flex items-center gap-2 text-xs text-[#A78BFA]">
      <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
      <span data-testid="checklist-scan-progress-step">{SCAN_STEP_LABELS[step] || "Scanning..."}</span>
    </div>
  );
}

function ChecklistEditorDialog({ open, onOpenChange, value, onChange, onSave, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checklist-editor-dialog" className="bg-[#111113] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Set checklist</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">One card per line: <code className="text-zinc-400">number, name</code> — e.g. <code className="text-zinc-400">4, Charizard</code></p>
        <Textarea
          data-testid="checklist-editor-textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={12}
          className="bg-black/30 border-white/10 font-mono-plex text-sm"
        />
        <DialogFooter>
          <Button data-testid="checklist-editor-save-button" onClick={onSave} disabled={saving} className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full">
            {saving ? "Saving..." : "Save checklist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
